/**
 * Skeleton loading screen for the sidebar conversation layout.
 *
 * Stands in for the whole panel, navigation bar included, so that nothing
 * shifts when the real header takes its place. In a host panel that header
 * carries a close control and the user's avatar.
 *
 * Estimates the viewport height to render enough `<SkeletonComment>`
 * placeholders to fill the visible area while the screen waits on its route
 * module and on the signed-in user.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HeaderBox, Pad, PadBox } from 'component/basics';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { SidebarNavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { PromptSkeleton } from 'component/progress/prompt-skeleton';
import { SkeletonComment } from 'component/progress/skeleton-comment';
import { SKELETON_NAV_BAR_HEIGHT } from 'component/progress/skeleton-nav-bar';
import { ConversationScreen } from 'component/screen';
import { useWindowDimensions, View } from 'react-native';

export function SkeletonConversationScreen() {
    const s = useStyles();
    const { colors } = useTheme();
    const { height } = useWindowDimensions();

    const HEADER_HEIGHT = 300;
    const COMMENT_HEIGHT = 115;
    const PAD_HEIGHT = 10;

    // estimate height to calculate how many comments to show
    const availableHeight = height - SKELETON_NAV_BAR_HEIGHT - HEADER_HEIGHT;
    const numSkeletonComments = Math.max(1, Math.floor((availableHeight - PAD_HEIGHT) / (COMMENT_HEIGHT + PAD_HEIGHT)));

    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <SidebarNavBarSkeleton />
            <ConversationScreen>
                <HeaderBox backgroundColor={colors.colorSurfaceSecondary}>
                    <Pad size={HEADER_HEIGHT} />
                </HeaderBox>
                <PromptSkeleton />
                <PadBox horiz={20} vert={20}>
                    {Array.from({ length: numSkeletonComments }).map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <View key={i}>
                            <SkeletonComment />
                            <Pad size={PAD_HEIGHT} />
                        </View>
                    ))}
                </PadBox>
            </ConversationScreen>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
}));
